import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { startCase } from 'lodash-es';
import { twMerge } from 'tailwind-merge';
import { faEllipsis } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { Sort } from '@components/Sort';
import { GlobalDropdown } from '@components/basics/Dropdowns/GlobalDropdown';
import { ExportCSVMenuItem } from '@components/basics/Dropdowns/MenuItems/ExportCSVMenuItem';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { List, TabContent, Tabs } from '@components/main-components/tabs/Tab';
import { useMediaQuery } from '@context/mediaQuery';
import { useToast } from '@context/toast';
import type { FragmentType } from '@graphql/reactQuery';
import { AudienceSongInfoFragmentDoc, TierNft } from '@graphql/reactQuery';
import { fetchQuery, getFragment, ReleaseBackersCsvUrlDocument } from '@graphql/reactQuery';
import { useSubscribeRelease } from '@hooks/useSubscribeRelease';
import { SectionTitle } from '../shared';
import { Audience } from './Audience';
import { AudienceTabTrigger } from './Trigger';
import type { AudienceSortType } from './constants';
import { AUDIENCE_SORT_TYPES } from './constants';

export function AudienceNew({
  releaseFrag,
  isFullPage,
}: {
  releaseFrag: FragmentType<AudienceSongInfoFragmentDoc>;
  isFullPage: boolean;
}) {
  const { tablet } = useMediaQuery();
  const [sort, setSort] = useState<AudienceSortType>('top');
  const release = getFragment(AudienceSongInfoFragmentDoc, releaseFrag);
  const { openToast } = useToast();

  const { data: subscriptionData } = useSubscribeRelease({
    release,
  });

  const hasAudience = subscriptionData.totalMinted > 0;

  const downloadCSV = async () => {
    try {
      const {
        data: { releaseBackersCSVUrl },
      } = await fetchQuery(ReleaseBackersCsvUrlDocument, {
        variables: {
          releaseId: release.id,
        },
      });
      if (releaseBackersCSVUrl) {
        window.open(releaseBackersCSVUrl);
      } else {
        throw Error('Could not find release backers CSV file');
      }
    } catch (e) {
      openToast({
        text: 'Download CSV release backers failed.',
        variant: 'error',
      });
    }
  };

  return (
    <div className={twMerge('flex h-full w-full flex-col gap-6', !isFullPage && 'mt-8 md:mt-16')}>
      <Tabs defaultValue={TierNft.All}>
        <div className="w-full">
          {!isFullPage && (
            <div className="flex w-full justify-between">
              <SectionTitle title="Audience" />
            </div>
          )}

          {hasAudience && (
            <div className="mt-4 flex w-full justify-between">
              <List className="flex flex-shrink-0 gap-2">
                <AudienceTabTrigger value={TierNft.All} icon={null}>
                  All
                </AudienceTabTrigger>
                <AudienceTabTrigger
                  value={TierNft.Vip}
                  icon={
                    <NextFutureImage
                      alt="starBadge"
                      src="/images/star-badge.png"
                      className="h-[18px] w-[18px]"
                      width={18}
                      height={18}
                    />
                  }
                >
                  {tablet ? 'Limited Edition' : 'Limited'}
                </AudienceTabTrigger>
                <AudienceTabTrigger value={TierNft.Ga} icon={null}>
                  {tablet ? 'Forever Edition' : 'Forever'}
                </AudienceTabTrigger>
              </List>

              <div className="flex items-center gap-5">
                <div className="w-[90px]">
                  <Sort
                    value={sort}
                    sortTypes={AUDIENCE_SORT_TYPES}
                    onValueChange={sortType => setSort(sortType)}
                    parseValue={sortType => startCase(sortType)}
                    showIcon={false}
                  />
                </div>

                {/* For now do not show export CSV on mobile devices */}
                <div className="hidden md:block">
                  <GlobalDropdown
                    align="end"
                    css={{ width: 'auto' }}
                    trigger={<IconButton icon={<FontAwesomeIcon icon={faEllipsis} />} />}
                  >
                    <ExportCSVMenuItem onClick={downloadCSV} />
                  </GlobalDropdown>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="relative mt-6 flex flex-col overflow-visible">
          <TabContent value={TierNft.All}>
            <Audience releaseId={release.id} tier={TierNft.All} sort={sort} paginate={isFullPage} />
          </TabContent>
          <TabContent value={TierNft.Vip}>
            <Audience releaseId={release.id} tier={TierNft.Vip} sort={sort} paginate={isFullPage} />
          </TabContent>
          <TabContent value={TierNft.Ga}>
            <Audience releaseId={release.id} tier={TierNft.Ga} sort={sort} paginate={isFullPage} />
          </TabContent>
        </div>
      </Tabs>
    </div>
  );
}
